import * as XLSX from 'xlsx';
import * as mammoth from 'mammoth/mammoth.browser';
import { getDocument, GlobalWorkerOptions, type TextItem } from 'pdfjs-dist/legacy/build/pdf';
import pdfWorkerSrc from 'pdfjs-dist/legacy/build/pdf.worker.min.js?url';
import { v4 as uuidv4 } from 'uuid';
import type { QuestionItem } from '@/types/api.types';
import { isExcelFile, isPdfFile, isWordFile } from './fileValidation';

/**
 * A question parsed out of an uploaded source document, with optional
 * positional metadata for tracing it back to its origin (Excel sheet
 * name + row, PDF page number, or `Document` for Word files).
 */
export interface ExtractedQuestion extends QuestionItem {
  rowNumber?: number;
  sheetName?: string;
}

/**
 * Outcome of an `extractQuestions*` call.
 *
 * `error` is set when `success` is `false`. `metadata` is set when
 * `success` is `true` and reports how many rows or lines were scanned
 * and which sheets, pages, or table groups produced the questions.
 */
export interface ExtractionResult {
  success: boolean;
  questions: ExtractedQuestion[];
  error?: string;
  metadata?: {
    totalRows: number;
    sheetsProcessed: string[];
  };
}

// Common column names that typically contain questions
const QUESTION_COLUMN_PATTERNS = [
  /^question$/i,
  /^questions$/i,
  /^ddq.?question/i,
  /^query$/i,
  /^item$/i,
  /^description$/i,
  /^requirement$/i,
];

GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

function findQuestionColumn(headers: string[]): number {
  // First, try exact matches with patterns
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i]?.toString().trim() || '';
    for (const pattern of QUESTION_COLUMN_PATTERNS) {
      if (pattern.test(header)) {
        return i;
      }
    }
  }

  // Fallback: look for any column containing "question"
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i]?.toString().toLowerCase() || '';
    if (header.includes('question')) {
      return i;
    }
  }

  // Last resort: use the first non-empty column
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i]?.toString().trim() || '';
    if (header.length > 0) {
      return i;
    }
  }

  return 0;
}

/**
 * Find the question column by scanning content patterns in data rows.
 * Used when headers don't clearly identify the question column.
 */
function findQuestionColumnByContent(rows: unknown[][]): number {
  if (rows.length < 3) return 1; // Default to column B if not enough data

  // Sample rows 2-10 to find column with question-like content
  const sampleRows = rows.slice(1, Math.min(15, rows.length));
  const columnScores: number[] = [];

  // Get max columns from sample
  const maxCols = Math.max(...sampleRows.map((r) => (Array.isArray(r) ? r.length : 0)));

  for (let col = 0; col < maxCols; col++) {
    let score = 0;
    let nonEmptyCount = 0;

    for (const row of sampleRows) {
      const cells = Array.isArray(row) ? row : [];
      const cellValue = cells[col]?.toString().trim() || '';

      if (!cellValue) continue;
      nonEmptyCount++;

      // Numbered questions (1., 2., 1a., etc.)
      if (/^[0-9]+[a-z]?\.?\s+/i.test(cellValue)) score += 3;
      // Starts with question words
      if (/^(what|who|where|when|why|how|please|describe|provide|is|are|do|does|have|has)/i.test(cellValue)) score += 2;
      // Ends with question mark
      if (cellValue.endsWith('?')) score += 3;
      // Long text (likely question content)
      if (cellValue.length > 50) score += 1;
    }

    // Normalize by non-empty count
    columnScores[col] = nonEmptyCount > 0 ? score / nonEmptyCount : 0;
  }

  // Find column with highest score, preferring earlier columns on tie
  let bestCol = 1; // Default to column B (index 1)
  let bestScore = columnScores[1] || 0;

  for (let col = 0; col < columnScores.length; col++) {
    if (columnScores[col] > bestScore) {
      bestScore = columnScores[col];
      bestCol = col;
    }
  }

  return bestCol;
}

/**
 * Check if a sheet looks like an introduction/instructions sheet to skip
 */
function isIntroductionSheet(sheetName: string, firstRows: unknown[][]): boolean {
  const nameLower = sheetName.toLowerCase();
  if (nameLower.includes('intro') || nameLower.includes('instruction') || nameLower.includes('guide') || nameLower.includes('overview')) {
    return true;
  }

  // Check if first few rows look like intro content (no numbered questions)
  const sampleText = firstRows
    .slice(0, 10)
    .flat()
    .filter(Boolean)
    .map((c) => String(c))
    .join(' ')
    .toLowerCase();

  // If no numbered patterns in first 10 rows, likely intro
  if (!/[0-9]+\.\s/.test(sampleText) && !sampleText.includes('question')) {
    return true;
  }

  return false;
}

function isValidQuestion(text: string): boolean {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  // Question should be at least 10 characters and not just numbers/special chars
  return trimmed.length >= 10 && /[a-zA-Z]/.test(trimmed);
}

/**
 * Check if text looks like an actual question rather than a statement/answer.
 * Returns a score: higher = more likely to be a question.
 */
function getQuestionScore(text: string): number {
  const trimmed = text.trim();
  let score = 0;

  // === STRONG POSITIVE: DDQ question patterns ===
  // "Please describe/list/provide/attach/summarize/fill in..." - very common DDQ pattern
  if (/^please\s+(describe|explain|provide|list|attach|summarize|fill|complete|indicate|specify|outline|detail|identify|confirm|state|give|submit|include|elaborate|comment|address|note|clarify|discuss|review)/i.test(trimmed)) {
    score += 15;
  }
  // Ends with question mark
  if (trimmed.endsWith('?')) score += 10;
  // Starts with question words
  if (/^(what|who|where|when|why|how|which|whose)/i.test(trimmed)) score += 8;
  // Yes/No question starters
  if (/^(is|are|do|does|can|could|would|should|will|have|has|did|was|were)\s+/i.test(trimmed)) score += 6;
  // Action verbs that request information
  if (/^(describe|explain|provide|list|outline|detail|identify|specify|summarize|elaborate|confirm|indicate|state)/i.test(trimmed)) score += 6;
  // Question numbering patterns (Q1., 1., 1), a., etc.)
  if (/^(q\s*[0-9]+|[0-9]+[.)]\s|[a-z][.)]\s|\([0-9]+\)|\([a-z]\))/i.test(trimmed)) score += 3;

  // === STRONG NEGATIVE: Answer/placeholder patterns ===
  // Response placeholders
  if (/^\[response\s+required\]/i.test(trimmed)) score -= 20;
  if (/^\[.*required.*\]/i.test(trimmed)) score -= 15;
  // Radio button options (⚪︎ Yes, ⚪︎ No, etc.)
  if (/^[⚪○●◯]\s*/i.test(trimmed)) score -= 20;
  if (/^(yes|no|n\/a|not applicable|none)\.?$/i.test(trimmed)) score -= 15;
  // Instructions/comments (these describe how to answer, not questions)
  if (/^(instructions?|comment|note|mf\s*=|etf\s*=):/i.test(trimmed)) score -= 15;
  // Attachment references (answers, not questions)
  if (/^(please\s+see\s+attachment|see\s+attachment|refer\s+to)/i.test(trimmed)) score -= 10;
  // Conditional nested questions (usually follow-ups, not main questions)
  if (/\(equal\s+to\s+(yes|no)\)/i.test(trimmed)) score -= 5;

  // === MODERATE NEGATIVE: Section headers ===
  if (/^(section|part|appendix|exhibit|schedule|chapter)\s+[0-9ivx]+/i.test(trimmed)) score -= 10;
  // All caps short text (section headers like "ORGANIZATION", "PEOPLE")
  if (trimmed === trimmed.toUpperCase() && trimmed.length < 50 && !/\?$/.test(trimmed)) score -= 8;
  // Page numbers and pagination artifacts
  if (/page\s+\d+\s*(of|\/)\s*\d+/i.test(trimmed)) score -= 15;
  // Plain-text checkbox option patterns
  if (/^(yes\s+no|no\s+yes|yes\s*\/\s*no|no\s*\/\s*yes)$/i.test(trimmed)) score -= 15;
  // Very short text without question indicators
  if (trimmed.length < 20 && !/\?$/.test(trimmed) && !/^please/i.test(trimmed)) score -= 5;

  // === WEAK NEGATIVE: Long answers ===
  // Very long text without question mark is likely an answer
  if (trimmed.length > 300 && !/\?/.test(trimmed) && !/^please/i.test(trimmed)) score -= 5;

  return score;
}

/**
 * Check if text is likely a section header
 */
function isSectionHeader(text: string): boolean {
  const trimmed = text.trim();
  // All caps short text (ORGANIZATION, PEOPLE, FIRM ASSETS, etc.)
  if (trimmed === trimmed.toUpperCase() && trimmed.length < 60 && /^[A-Z]/.test(trimmed)) return true;
  // Section/Part numbering
  if (/^(section|part|appendix|exhibit|schedule|chapter)\s+[0-9ivx]+/i.test(trimmed)) return true;
  // Roman numerals only
  if (/^[IVX]+\.?\s*$/i.test(trimmed)) return true;
  // Common DDQ section names
  if (/^(organization|people|governance|ownership|compensation|compliance|risk|performance|assets|investment|operations|legal|regulatory|esg|environmental|social)/i.test(trimmed) && trimmed.length < 50) return true;
  return false;
}

/**
 * Check if text is a DDQ answer placeholder or non-question content
 */
function isNonQuestionContent(text: string): boolean {
  const trimmed = text.trim();
  // Response placeholders
  if (/^\[.*required.*\]/i.test(trimmed)) return true;
  // Radio buttons
  if (/^[⚪○●◯]\s*/i.test(trimmed)) return true;
  // Simple yes/no/n/a answers
  if (/^(yes|no|n\/a|not applicable|none|not available)\.?$/i.test(trimmed)) return true;
  // Short answers starting with yes/no
  if (/^(yes|no)[.,:]?\s/i.test(trimmed) && trimmed.length < 100) return true;
  // Instructions/comments
  if (/^(instructions?|comment|note):/i.test(trimmed)) return true;
  // Legend items (MF = Mutual Fund, etc.)
  if (/^[A-Z]{2,4}\s*=\s*/i.test(trimmed)) return true;
  // Attachment references (answers)
  if (/^(please\s+see\s+attachment|see\s+attachment|refer\s+to\s+attachment|attachment\s+[a-z])/i.test(trimmed)) return true;
  // URLs
  if (/^https?:\/\//i.test(trimmed)) return true;
  // "Downloaded and viewed" type confirmations
  if (/^(downloaded|confirmed|acknowledged|completed|attached)/i.test(trimmed)) return true;
  // Table headers (Name, Date, Title patterns)
  if (/^(name|date|title|previous|new|#|%)\s*(of|\/)?/i.test(trimmed) && trimmed.length < 40) return true;
  // Person names (First Last pattern without question words)
  if (/^[A-Z][a-z]+\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)?$/.test(trimmed) && trimmed.length < 50) return true;
  // "Not applicable" variations
  if (/^not\s+(applicable|available|disclosed)/i.test(trimmed)) return true;
  // Answers that are just descriptions (This/The/Our/We patterns)
  if (/^(this|the|our|we|as|in|for|all|each|levels|details|audit)/i.test(trimmed) && !/\?$/.test(trimmed) && trimmed.length < 80) return true;
  // Copyright/legal notices
  if (/^©|copyright/i.test(trimmed)) return true;
  // Required attachments checklist
  if (/^□|^required\s+attachments/i.test(trimmed)) return true;
  // Short "Please acknowledge/check" that aren't real questions
  if (/^please\s+(acknowledge|check|download|see)/i.test(trimmed) && trimmed.length < 50) return true;
  // Page numbers and pagination (header/footer artifacts)
  if (/^page\s+\d+(\s*(of|\/)\s*\d+)?$/i.test(trimmed)) return true;
  if (/page\s+\d+\s*(of|\/)\s*\d+/i.test(trimmed) && trimmed.length < 40) return true;
  // Confidential/proprietary notices
  if (/^(confidential|proprietary|for\s+internal\s+use|do\s+not\s+distribute)/i.test(trimmed)) return true;
  // Date-only lines (header/footer dates)
  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(trimmed)) return true;
  // Plain-text checkbox option patterns (Yes No, No Yes, Yes / No)
  if (/^(yes\s+no|no\s+yes|yes\s*\/\s*no|no\s*\/\s*yes)$/i.test(trimmed)) return true;
  // Checkbox symbols with yes/no/n/a
  if (/^[☐☑✓✗✘]\s*(yes|no|n\/a)/i.test(trimmed)) return true;
  return false;
}

function normalizeQuestion(text: string): string {
  return text
    .replace(/^[\s\u2022*-]+/g, '')
    .replace(/^\(?[0-9]+[.)]\s+/, '')
    .replace(/^[a-zA-Z][.)]\s+/, '')
    .replace(/^(question|q)\s*[:.-]\s*/i, '')
    .trim();
}

function splitLineIntoCandidates(line: string): string[] {
  const parts = line.split(/(?<=\?)\s+/);
  if (parts.length > 1) {
    return parts;
  }
  return [line];
}

function extractQuestionsFromText(
  text: string,
  options?: { sourceLabel?: string; dedupeSet?: Set<string> }
): ExtractedQuestion[] {
  const questions: ExtractedQuestion[] = [];
  const dedupe = options?.dedupeSet ?? new Set<string>();

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const pushCandidate = (candidate: string, rowNumber?: number) => {
    const normalized = normalizeQuestion(candidate);
    if (!isValidQuestion(normalized)) return;

    // Skip section headers and non-question content
    if (isSectionHeader(candidate)) return;
    if (isNonQuestionContent(candidate)) return;

    // Score the candidate - only keep if it looks like a question
    // Require positive score to filter out ambiguous content
    const score = getQuestionScore(normalized);
    if (score <= 0) return; // Must have positive indicators of being a question

    const key = normalized.toLowerCase();
    if (dedupe.has(key)) return;
    dedupe.add(key);
    questions.push({
      question_id: uuidv4(),
      question: normalized,
      rowNumber,
      sheetName: options?.sourceLabel,
    });
  };

  if (lines.length > 1) {
    for (let i = 0; i < lines.length; i++) {
      for (const part of splitLineIntoCandidates(lines[i])) {
        pushCandidate(part, i + 1);
      }
    }
  } else {
    const fallbackParts = text.split(/(?<=\?)\s+/);
    if (fallbackParts.length > 1) {
      fallbackParts.forEach((part, index) => pushCandidate(part, index + 1));
    } else {
      text
        .split(/[\u2022;]\s+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part, index) => pushCandidate(part, index + 1));
    }
  }

  return questions;
}

/**
 * Extract questions from an Excel workbook (`.xlsx` or `.xls`).
 *
 * Iterates each sheet, skips sheets identified as introduction or
 * instruction pages, finds the question column from the header row
 * (falling back to content-pattern scoring when headers are unhelpful),
 * and keeps rows whose text passes the question-shape heuristics
 * (`isValidQuestion`, `isSectionHeader`, `isNonQuestionContent`,
 * `getQuestionScore > 0`). Always resolves: parser exceptions and
 * `FileReader` errors are reported as `success: false` results, never
 * thrown.
 *
 * @param file - The Excel file selected by the user.
 * @returns A promise resolving to an `ExtractionResult`. On success,
 *   `questions` is non-empty and `metadata.sheetsProcessed` lists the
 *   sheets that contributed at least one question. On failure, `error`
 *   describes why (no questions found, parse error, or read error).
 */
export async function extractQuestionsFromExcel(file: File): Promise<ExtractionResult> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });

        const questions: ExtractedQuestion[] = [];
        const sheetsProcessed: string[] = [];
        let totalRows = 0;

        // Process each sheet
        for (const sheetName of workbook.SheetNames) {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json<unknown[]>(worksheet, { header: 1 });

          if (jsonData.length < 2) continue; // Need at least 2 rows

          // Skip introduction/instruction sheets
          if (isIntroductionSheet(sheetName, jsonData as unknown[][])) {
            continue;
          }

          sheetsProcessed.push(sheetName);
          totalRows += jsonData.length;

          // Try to find question column from headers first
          const headerRow = jsonData[0];
          const headers = Array.isArray(headerRow) ? headerRow.map((h) => String(h || '')) : [];
          let questionColIndex = findQuestionColumn(headers);

          // If header detection returned column 0 but it's empty, scan content
          const col0Values = jsonData.slice(0, 10).map((r) => (Array.isArray(r) ? r[0] : null)).filter(Boolean);
          if (questionColIndex === 0 && col0Values.length === 0) {
            questionColIndex = findQuestionColumnByContent(jsonData as unknown[][]);
          }

          // Extract questions from all rows (some files don't have header rows)
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            const cells = Array.isArray(row) ? row : [];
            const questionText = cells[questionColIndex]?.toString().trim() || '';

            if (!isValidQuestion(questionText)) continue;
            if (isSectionHeader(questionText)) continue;
            if (isNonQuestionContent(questionText)) continue;

            // Use scoring to filter
            const score = getQuestionScore(questionText);
            if (score <= 0) continue;

            // Normalize the question (remove numbering prefix for cleaner storage)
            const normalized = normalizeQuestion(questionText);
            if (!isValidQuestion(normalized)) continue;

            questions.push({
              question_id: uuidv4(),
              question: normalized,
              rowNumber: i + 1, // 1-indexed for user display
              sheetName,
            });
          }
        }

        if (questions.length === 0) {
          resolve({
            success: false,
            questions: [],
            error: 'No valid questions found in the uploaded file. Please ensure your file has a column containing DDQ questions.',
          });
          return;
        }

        resolve({
          success: true,
          questions,
          metadata: {
            totalRows,
            sheetsProcessed,
          },
        });
      } catch (error) {
        resolve({
          success: false,
          questions: [],
          error: `Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    };

    reader.onerror = () => {
      resolve({
        success: false,
        questions: [],
        error: 'Failed to read file',
      });
    };

    reader.readAsBinaryString(file);
  });
}

/**
 * Extract questions from HTML tables (common DDQ format).
 * Looks for tables with Question/Answer column structure.
 */
function extractQuestionsFromHtmlTables(html: string): ExtractedQuestion[] {
  const questions: ExtractedQuestion[] = [];
  const dedupe = new Set<string>();

  // Parse tables from HTML
  const tableRegex = /<table[^>]*>([\s\S]*?)<\/table>/gi;
  let tableMatch;
  let tableIndex = 0;

  while ((tableMatch = tableRegex.exec(html)) !== null) {
    tableIndex++;
    const tableContent = tableMatch[1];

    // Extract rows
    const rowRegex = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
    const rows: string[][] = [];
    let rowMatch;

    while ((rowMatch = rowRegex.exec(tableContent)) !== null) {
      const rowContent = rowMatch[1];
      const cellRegex = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
      const cells: string[] = [];
      let cellMatch;

      while ((cellMatch = cellRegex.exec(rowContent)) !== null) {
        // Strip HTML tags and decode entities
        const cellText = cellMatch[1]
          .replace(/<[^>]+>/g, ' ')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/\s+/g, ' ')
          .trim();
        cells.push(cellText);
      }

      if (cells.length > 0) {
        rows.push(cells);
      }
    }

    if (rows.length < 2) continue;

    // Check if first row looks like headers
    const headers = rows[0].map((h) => h.toLowerCase());
    let questionColIndex = -1;

    // Find question column
    for (let i = 0; i < headers.length; i++) {
      if (
        /^questions?$/i.test(headers[i]) ||
        /question/i.test(headers[i]) ||
        /^query$/i.test(headers[i]) ||
        /^item$/i.test(headers[i])
      ) {
        questionColIndex = i;
        break;
      }
    }

    // If no question column found but table has 2+ columns, try first column
    if (questionColIndex === -1 && headers.length >= 2) {
      // Check if first column contains question-like content
      const firstColSample = rows.slice(1, 4).map((r) => r[0] || '');
      const hasQuestions = firstColSample.some(
        (text) => text.endsWith('?') || getQuestionScore(text) > 0
      );
      if (hasQuestions) {
        questionColIndex = 0;
      }
    }

    if (questionColIndex === -1) continue;

    // Extract questions from data rows
    for (let i = 1; i < rows.length; i++) {
      const questionText = rows[i][questionColIndex]?.trim() || '';
      const normalized = normalizeQuestion(questionText);

      if (!isValidQuestion(normalized)) continue;
      if (isSectionHeader(questionText)) continue;
      if (isNonQuestionContent(questionText)) continue;
      if (getQuestionScore(normalized) <= 0) continue;

      const key = normalized.toLowerCase();
      if (dedupe.has(key)) continue;
      dedupe.add(key);

      questions.push({
        question_id: uuidv4(),
        question: normalized,
        rowNumber: i,
        sheetName: `Table ${tableIndex}`,
      });
    }
  }

  return questions;
}

/**
 * Extract questions from HTML headings and paragraphs.
 * Uses heading hierarchy to identify sections vs questions.
 */
function extractQuestionsFromHtmlContent(html: string): ExtractedQuestion[] {
  const questions: ExtractedQuestion[] = [];
  const dedupe = new Set<string>();

  // Remove tables (handled separately)
  const contentWithoutTables = html.replace(/<table[^>]*>[\s\S]*?<\/table>/gi, '');

  // Extract text from paragraphs and list items (skip h1/h2 which are usually section headers)
  const contentRegex = /<(p|li|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/\1>/gi;
  let match;
  let lineNumber = 0;

  while ((match = contentRegex.exec(contentWithoutTables)) !== null) {
    lineNumber++;
    const tagName = match[1].toLowerCase();
    const content = match[2]
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/\s+/g, ' ')
      .trim();

    if (!content) continue;

    // Skip non-question content early
    if (isNonQuestionContent(content)) continue;

    // h3+ headings in DDQs are often questions
    const isHeading = tagName.startsWith('h');
    const normalized = normalizeQuestion(content);

    if (!isValidQuestion(normalized)) continue;
    if (isSectionHeader(content)) continue;

    // For headings, be more lenient with scoring
    const score = getQuestionScore(normalized);
    if (!isHeading && score <= 0) continue;
    if (isHeading && score < -3) continue;

    const key = normalized.toLowerCase();
    if (dedupe.has(key)) continue;
    dedupe.add(key);

    questions.push({
      question_id: uuidv4(),
      question: normalized,
      rowNumber: lineNumber,
      sheetName: 'Document',
    });
  }

  return questions;
}

/**
 * Extract questions from a Word document (`.docx`).
 *
 * Converts the document to HTML via `mammoth`, then runs two structured
 * passes: first `extractQuestionsFromHtmlTables` (DDQ-style two-column
 * tables), then `extractQuestionsFromHtmlContent` (`<p>`, `<li>`, and
 * `h3+` headings), deduplicating between the passes by lowercased
 * question text. If both passes return zero questions, falls back to
 * `mammoth.extractRawText` and re-runs `extractQuestionsFromText` over
 * the plain text. Errors thrown by `mammoth` are caught and returned
 * as `success: false` results.
 *
 * @param file - The Word file selected by the user.
 * @returns A promise resolving to an `ExtractionResult`. On success,
 *   `metadata.sheetsProcessed` is `['Document']`. On failure, `error`
 *   describes why.
 */
export async function extractQuestionsFromWord(file: File): Promise<ExtractionResult> {
  try {
    const arrayBuffer = await file.arrayBuffer();

    // First try HTML extraction which preserves structure (tables, headings)
    const htmlResult = await mammoth.convertToHtml({ arrayBuffer });
    const html = htmlResult.value ?? '';

    let questions: ExtractedQuestion[] = [];

    // Extract from tables first (most structured)
    const tableQuestions = extractQuestionsFromHtmlTables(html);
    questions.push(...tableQuestions);

    // Then extract from paragraphs/headings
    const contentQuestions = extractQuestionsFromHtmlContent(html);

    // Dedupe against table questions
    const existingKeys = new Set(questions.map((q) => q.question.toLowerCase()));
    for (const q of contentQuestions) {
      if (!existingKeys.has(q.question.toLowerCase())) {
        questions.push(q);
        existingKeys.add(q.question.toLowerCase());
      }
    }

    // Fallback to raw text if no questions found
    if (questions.length === 0) {
      const textResult = await mammoth.extractRawText({ arrayBuffer });
      const text = textResult.value ?? '';
      questions = extractQuestionsFromText(text, { sourceLabel: 'Document' });
    }

    if (questions.length === 0) {
      return {
        success: false,
        questions: [],
        error: 'No valid questions found in the Word document.',
      };
    }

    return {
      success: true,
      questions,
      metadata: {
        totalRows: questions.length,
        sheetsProcessed: ['Document'],
      },
    };
  } catch (error) {
    return {
      success: false,
      questions: [],
      error: `Failed to parse Word file: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Merge continuation lines that are part of the same question.
 * PDF line breaks are visual (from page width), not semantic.
 */
function joinContinuationLines(text: string): string {
  const lines = text.split('\n');
  const merged: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      merged.push('');
      continue;
    }

    if (merged.length === 0 || merged[merged.length - 1] === '') {
      merged.push(line);
      continue;
    }

    const prevLine = merged[merged.length - 1];

    // Don't join if this line starts a new question (numbered or lettered)
    if (/^(\d+[.)]\s|[a-z][.)]\s|Q\d)/i.test(line)) {
      merged.push(line);
      continue;
    }

    // Don't join if this line starts with a question word AND looks like a complete question start
    // (only block join if the line is long enough to be a standalone question, not a short fragment)
    if (
      /^(What|How|When|Where|Why|Who|Which|Does|Do|Is|Are|Has|Have|Can|Could|Would|Should|Will|Please)\s/i.test(line) &&
      line.length >= 40
    ) {
      merged.push(line);
      continue;
    }

    // Don't join if previous line ends with sentence-terminating punctuation
    // BUT allow joining if the previous line is very short (< 40 chars) and doesn't end with '?'
    // — this catches PDF line wraps mid-question like "Is the identical strategy used aside from the"
    if (/[.?!]\s*$/.test(prevLine) && (prevLine.length >= 40 || /\?\s*$/.test(prevLine))) {
      merged.push(line);
      continue;
    }
    // Colons are weaker terminators — only split if previous line is reasonably long
    if (/:\s*$/.test(prevLine) && prevLine.length >= 50) {
      merged.push(line);
      continue;
    }

    // If the current line starts lowercase or with an article/preposition, it's very likely a continuation
    // Even if previous line ended with punctuation, short fragments are probably wrapped mid-sentence
    const isContinuationStart = /^[a-z]/.test(line) ||
      /^(the|a|an|of|in|to|for|and|or|with|by|from|at|on|as|but|that|this|which|their|its|our|your)\s/i.test(line);

    if (isContinuationStart && prevLine.length < 60 && !/\?\s*$/.test(prevLine)) {
      // Strong continuation signal — merge even if other heuristics would split
      merged[merged.length - 1] = prevLine + ' ' + line;
      continue;
    }

    // Don't join lines that look like page headers/footers
    if (/page\s+\d+/i.test(line) && line.length < 40) {
      merged.push(line);
      continue;
    }
    // Don't join short all-caps lines (likely section headers)
    if (line === line.toUpperCase() && line.length < 50 && /^[A-Z]/.test(line)) {
      merged.push(line);
      continue;
    }
    // Don't join date-only lines (footer artifacts)
    if (/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(line)) {
      merged.push(line);
      continue;
    }

    // Default: continuation — join with space
    merged[merged.length - 1] = prevLine + ' ' + line;
  }

  return merged.join('\n');
}

/**
 * Extract questions from a PDF (`.pdf`).
 *
 * Loads the PDF through `pdf.js`, walks each page in order, and
 * concatenates the page's text items into a single string (inserting
 * newlines on `hasEOL` and spaces otherwise). `joinContinuationLines`
 * then collapses visually-wrapped lines back into single semantic
 * lines, since PDF line breaks are layout-driven rather than
 * sentence-driven. Each page is fed through the shared
 * `extractQuestionsFromText`, sharing one `dedupeSet` across all pages
 * so duplicates that span page breaks are dropped. Errors thrown by
 * `pdf.js` are caught and returned as `success: false` results.
 *
 * @param file - The PDF file selected by the user.
 * @returns A promise resolving to an `ExtractionResult`. On success,
 *   `metadata.sheetsProcessed` contains one `Page N` entry per page in
 *   the document. On failure, `error` describes why.
 */
export async function extractQuestionsFromPdf(file: File): Promise<ExtractionResult> {
  try {
    const data = await file.arrayBuffer();
    const loadingTask = getDocument({ data });
    const pdf = await loadingTask.promise;
    const questions: ExtractedQuestion[] = [];
    const dedupe = new Set<string>();
    const pagesProcessed: string[] = [];
    let totalRows = 0;

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const pageLabel = `Page ${pageNumber}`;
      pagesProcessed.push(pageLabel);

      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      let pageText = '';

      for (const item of textContent.items as TextItem[]) {
        const textItem = item as TextItem;
        pageText += textItem.str || '';
        pageText += textItem.hasEOL ? '\n' : ' ';
      }

      // Join continuation lines before extraction — PDF line breaks are visual, not semantic
      pageText = joinContinuationLines(pageText);

      totalRows += pageText.split(/\r?\n/).filter(Boolean).length;
      const pageQuestions = extractQuestionsFromText(pageText, {
        sourceLabel: pageLabel,
        dedupeSet: dedupe,
      });
      questions.push(...pageQuestions);
    }

    if (questions.length === 0) {
      return {
        success: false,
        questions: [],
        error: 'No valid questions found in the PDF.',
      };
    }

    return {
      success: true,
      questions,
      metadata: {
        totalRows,
        sheetsProcessed: pagesProcessed,
      },
    };
  } catch (error) {
    return {
      success: false,
      questions: [],
      error: `Failed to parse PDF file: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Dispatch a file to the appropriate extractor based on its extension.
 *
 * Uses `isExcelFile`, `isWordFile`, and `isPdfFile` from
 * `./fileValidation` to pick `extractQuestionsFromExcel`,
 * `extractQuestionsFromWord`, or `extractQuestionsFromPdf` in that
 * order. Files that match none return an `Unsupported file format`
 * failure result.
 *
 * @param file - The user-selected file.
 * @returns A promise resolving to the `ExtractionResult` produced by
 *   the selected extractor, or a synthetic failure result if the
 *   extension is not supported.
 */
export async function extractQuestions(file: File): Promise<ExtractionResult> {
  if (isExcelFile(file)) {
    return extractQuestionsFromExcel(file);
  }

  if (isWordFile(file)) {
    return extractQuestionsFromWord(file);
  }

  if (isPdfFile(file)) {
    return extractQuestionsFromPdf(file);
  }

  return {
    success: false,
    questions: [],
    error: 'Unsupported file format',
  };
}
